import { defineStore } from "pinia";
import { ref } from "vue";

import { relativeFromViewport } from "@/math/coordinate-utils";
import type { IdMap } from "@/model/baseTypes";
import type { Card } from "@/model/card";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";

export interface Drag {
  id: string;
  pos: RelativeCoordinate;
  grabFromOutside?: boolean;
  autoFocus?: boolean;
}

interface ExternalDragMetadata {
  grabFromOutside: boolean;
  autoFocus: boolean;
  initialPos: ViewportCoordinate;
}

export const useDraggingStore = defineStore("dragging", () => {
  const dragging = ref<IdMap<Drag>>({});
  const externalDragMetadata = new Map<string, ExternalDragMetadata>();

  function findById(id: string): Drag | undefined {
    for (const dragId in dragging.value) {
      if (dragging.value[dragId].id === id) {
        return dragging.value[dragId];
      }
    }
  }

  function initCardDrag(card: Card): { grabFromOutside?: boolean } {
    // Check if this card has external drag metadata (from useDragToCreateSticky)
    const externalMetadata = externalDragMetadata.get(card.id);
    if (!externalMetadata) {
      // Normal drag from board - check if already in dragging store
      return findById(card.id) ?? {};
    }

    // Card was created externally and needs dragging state initialized
    // Initialize dragging state NOW (when drag gesture actually starts)
    const pos = relativeFromViewport(externalMetadata.initialPos);
    startCardDragging(card.id, pos, externalMetadata);
    externalDragMetadata.delete(card.id);
    return externalMetadata;
  }

  function startCardDragging(
    id: string,
    pos: RelativeCoordinate,
    options: { grabFromOutside?: boolean; autoFocus?: boolean } = {},
  ) {
    const board = useBoardStore().currentBoard();
    const boardCard = board.cards[id];
    boardCard.meta.dragging = true;
    if (!(id in dragging.value)) {
      dragging.value[id] = {
        id,
        grabFromOutside: !!options.grabFromOutside,
        autoFocus: !!options.autoFocus,
        pos,
      };
    }
  }

  function endCardDragging(id: string, pos: RelativeCoordinate) {
    delete dragging.value[id];
    const boardCard = useBoardStore().currentBoard().cards[id];
    if (!boardCard) {
      return;
    }
    boardCard.meta.pos.x = pos.x;
    boardCard.meta.pos.y = pos.y;
    boardCard.meta.zIndex = ++useBoardStore().currentBoard().maxZ;
    boardCard.meta.dragging = false;
  }

  return {
    dragging: asReadonly(dragging),

    findById,
    initCardDrag,
    startCardDragging,
    dragCard: (id: string, pos: RelativeCoordinate) => (dragging.value[id].pos = pos),
    endCardDragging,
    setExternalDragMetadata: (cardId: string, metadata: ExternalDragMetadata) =>
      void externalDragMetadata.set(cardId, metadata),
  };
});
